/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ContextualHelp, Grid, Typography2 } from '@coursera/cds-core';

import Rating from 'bundles/unified-description-page-common/components/shared/Rating';
import InstructorItem from 'bundles/unified-description-page-common/components/shared/instructors/InstructorItem';
import Modal from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import type { ModalProps } from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import type {
  InstructorFragment,
  PartnerFragment,
} from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import getRelatedPartnerName from 'bundles/unified-description-page-common/utils/getRelatedPartnerName';

import _t from 'i18n!nls/unified-description-page-common';

const styles = {
  container: css`
    display: flex;
    align-items: center;
  `,
  instructorContainer: css`
    margin-top: var(--cds-spacing-400);
  `,
  ratingsSpacer: css`
    gap: var(--cds-spacing-200);

    span {
      align-self: center;
    }
  `,
};

type Props = ModalProps & {
  instructors: Array<InstructorFragment>;
  partners?: Array<PartnerFragment> | null;
  instructorRatingCount?: number | null;
  averageInstructorRating?: number | null;
  disableCta?: boolean;
};

const InstructorsModal = ({
  instructorRatingCount,
  averageInstructorRating,
  instructors,
  partners,
  disableCta,
  ...rest
}: Props) => {
  return (
    <Modal {...rest} header={_t('Instructors')}>
      {Boolean(instructorRatingCount) && Boolean(averageInstructorRating) && (
        <React.Fragment>
          <ContextualHelp
            helpToggleProps={{
              'aria-label': _t('Information about the instructor ratings'),
            }}
            tooltipProps={{
              closeButtonProps: {
                'aria-label': _t('Close information about the instructor ratings'),
              },
            }}
            label={
              <Typography2 component="p" variant="bodyPrimary">
                {_t('Instructor ratings')}
              </Typography2>
            }
          >
            <Typography2 component="p" variant="bodyPrimary">
              {_t(
                'We asked all learners to give feedback on our instructors based on the quality of their teaching style.'
              )}
            </Typography2>
          </ContextualHelp>
          <div css={styles.container}>
            <Rating
              rating={averageInstructorRating}
              ratingCount={instructorRatingCount}
              size="large"
              customStyles={styles.ratingsSpacer}
            />
          </div>
        </React.Fragment>
      )}
      <Grid container>
        {instructors.map((instructor) => (
          <Grid item md={6} xs={12} css={styles.instructorContainer} key={instructor.id}>
            <InstructorItem
              instructor={instructor}
              relatedPartnerName={getRelatedPartnerName(instructor.id, partners)}
              disableCTA={disableCta}
            />
          </Grid>
        ))}
      </Grid>
    </Modal>
  );
};

export default InstructorsModal;
